/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { useState, useEffect, useRef, FormEvent } from 'react';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import uniqBy from 'lodash/uniqBy';
import { Item, Key } from 'react-stately';
import { useNavigate } from 'react-router';

import { HeroImageSectionFront, Icon, Button, Autosuggest } from 'sarsaparilla';

import { searchLocations } from '../../api';
import { venuePageRoute } from '../../../dev/routesArray';
import type { ParentLocation, TreePermitData } from '../../../types/permit';

const goToSmooth = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    window.history.pushState('', '', `#${id}`);
};

const NO_RESULTS_KEY = 'tree-permit-search-no-results';
const MAX_QUERY_DISPLAY_LENGTH = 8;

interface HeroWithAutoCompleteType {
    locations: Array<Partial<TreePermitData>>;
}

function HeroWithAutoComplete({ locations = [] }: HeroWithAutoCompleteType) {
    const navigate = useNavigate();
    const [value, setValue] = useState('');
    const [suggestions, setSuggestions] = useState<Array<Partial<TreePermitData>>>([]);
    const [localSuggestions, setLocalSuggestions] = useState<
        Array<Partial<TreePermitData>>
    >([]);
    const [searchSuggestions, setSearchSuggestions] = useState<
        Array<Partial<TreePermitData>>
    >([]);
    const [selectedLocation, setSelectedLocation] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    // responses can arrive out of order, so only the latest query's response is used
    const latestQueryRef = useRef('');

    const goToLocation = () => {
        if (!isEmpty(selectedLocation)) {
            navigate(venuePageRoute(selectedLocation));
        }
    };

    const getSuggestions = (query: string) => {
        const search = query.trim().toLowerCase();

        if (search === '') {
            return [];
        }

        const matches = (location: Partial<TreePermitData>, key: 'state' | 'name') =>
            (location[key] ?? '').toLowerCase().includes(search);

        return locations.filter(
            (location) => matches(location, 'state') || matches(location, 'name')
        );
    };

    const updateLocalSuggestions = (query: string) => {
        const _suggestions = getSuggestions(query);

        setLocalSuggestions(_suggestions);
        setSuggestions(_suggestions);
    };

    const makeSearch = (_value: string) => {
        latestQueryRef.current = _value;

        if (_value.trim() === '') {
            setSearchSuggestions([]);
            setIsSearching(false);
            return;
        }

        const isLatest = () => latestQueryRef.current === _value;

        setIsSearching(true);
        searchLocations(_value)
            .then(({ data }: { data: { results?: Array<ParentLocation> } }) => {
                if (!isLatest()) return;

                const results = get(data, 'results', []).map(
                    (item: Partial<ParentLocation>) => ({ ...item, id: item?.entity_id })
                );

                setSearchSuggestions(results);
            })
            .catch(() => {
                if (isLatest()) setSearchSuggestions([]);
            })
            .finally(() => {
                if (isLatest()) setIsSearching(false);
            });
    };

    const onSuggestionsClearRequested = () => {
        latestQueryRef.current = '';
        setValue('');
        setIsSearching(false);
        setSearchSuggestions([]);
        setLocalSuggestions([]);
        setSuggestions([]);
    };

    useEffect(() => {
        setSuggestions(uniqBy([...searchSuggestions, ...localSuggestions], 'id'));
    }, [searchSuggestions, localSuggestions]);

    const handleInputChange = (_value: string) => {
        setValue(_value);
        makeSearch(_value);
        updateLocalSuggestions(_value);
    };

    const handleOnSelect = (key: Key, suggestion?: Partial<TreePermitData>) => {
        if (key !== NO_RESULTS_KEY && suggestion?.id) {
            setSelectedLocation(suggestion.id);
        }
    };

    const query = value.trim();
    const hasNoResults = query !== '' && !isSearching && suggestions.length === 0;
    const displayQuery =
        query.length > MAX_QUERY_DISPLAY_LENGTH
            ? `${query.slice(0, MAX_QUERY_DISPLAY_LENGTH)}...`
            : query;
    const noResultsMessage = `No results match "${displayQuery}". Try a state or forest name.`;
    const autosuggestItems = hasNoResults
        ? [{ id: NO_RESULTS_KEY, name: noResultsMessage }]
        : suggestions;

    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        goToLocation();
    };

    const renderSearchComponent = () => {
        return (
            <>
                <div className="tree-permit-splash-wrap-search">
                    <form className="form-search" onSubmit={handleSubmit}>
                        <div className="search-input-wrapper">
                            <div className="form-item-wrap" role="search">
                                <Autosuggest
                                    allowsEmptyCollection
                                    allowsCustomValue
                                    isLabelVisible={false}
                                    menuTrigger="focus"
                                    label="Search"
                                    placeholder="Search by state or forest name"
                                    items={autosuggestItems}
                                    disabledKeys={hasNoResults ? [NO_RESULTS_KEY] : []}
                                    onInputChange={handleInputChange}
                                    onSelect={handleOnSelect}
                                    onClear={onSuggestionsClearRequested}
                                >
                                    {(item) =>
                                        item.id === NO_RESULTS_KEY ? (
                                            <Item textValue={item.name}>
                                                <span className="tree-permit-search-no-results">
                                                    {item.name}
                                                </span>
                                            </Item>
                                        ) : (
                                            <Item>{item.name}</Item>
                                        )
                                    }
                                </Autosuggest>
                                <div aria-live="polite" className="rec-sr-only">
                                    {hasNoResults ? noResultsMessage : ''}
                                </div>
                            </div>
                        </div>
                        <Button
                            type="submit"
                            appearance="secondary"
                            size="lg"
                            className="hero-select-button"
                            gaTrackingId="071089442512"
                            gaTrackingValue={value || 'no value set'}
                        >
                            Get started
                        </Button>
                    </form>
                </div>
                <div className="tree-permit-splash-wrap-watch-list">
                    <Button
                        className="tree-permit-splash-wrap-watch-list-button"
                        iconBeforeElement={<Icon iconName="arrow-bottom" />}
                        appearance="pill"
                        size="xs"
                        onClick={() => goToSmooth('locations')}
                        gaTrackingId="779124299328"
                    >
                        View all Tree Permit Locations
                    </Button>
                </div>
            </>
        );
    };
    return (
        <HeroImageSectionFront
            buttonAppearance="secondary"
            hasUnderline={false}
            title="Get Your Christmas Tree Permit"
            className="tree-permit-splash-header"
            backgroundImagePath="/img/tree-permits/hero/tree-permits-hero"
            backgroundImageAltText="A group of people walking in a snow covered forest surrounded by snow capped mountains"
            backgroundImageColorOverlay="#fff"
            backgroundImageColorOverlayOpacity={0.2}
            render={renderSearchComponent}
        />
    );
}

export default HeroWithAutoComplete;
